/**
 * AddContractModal — edit an existing Construction Contract (Supabase).
 *
 * Contracts are created ONLY by awarding a bidder in a Bid Campaign (the award flow
 * creates the contract record). This modal is the editor reached from a contract's
 * ⋯ "Edit contract" action; it never creates a contract.
 *
 * Opens via CustomEvent('open-add-contract-modal', { detail: { contractId, prefill } }).
 * Busts ctx.contractCache + dispatches outline-render on success.
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { updateContract, CONTRACT_STATUS_LABELS, ACTIVE_CONTRACT_CONFLICT } from '../services/bidCampaigns.js'
import { getOrganizations } from '../services/contacts.js'
import Select from './Select.jsx'
import { confirmDialog } from './Dialog.jsx'

const STATUS_OPTIONS = Object.keys(CONTRACT_STATUS_LABELS).map(v => ({ value: v, label: CONTRACT_STATUS_LABELS[v] }))
const CONTRACT_TYPES = ['Lump Sum', 'Cost-Plus', 'Cost-Plus with GMP', 'Time and Materials', 'Design-Build', 'Construction Manager at Risk']

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }
const areaStyle = { ...inputStyle, minHeight: 56, resize: 'vertical', lineHeight: 1.5 }

export default function AddContractModal() {
  const [open, setOpen]             = useState(false)
  const [editId, setEditId]         = useState(null)
  const [contractor, setContractor] = useState('')
  const [type, setType]             = useState('')
  const [value, setValue]           = useState('')
  const [startDate, setStartDate]   = useState('')
  const [buildDate, setBuildDate]   = useState('')
  const [subDate, setSubDate]       = useState('')
  const [status, setStatus]         = useState('active')
  const [notes, setNotes]           = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState(null)

  // Linked GC Organization (contractor_airtable_id) — drives the derived "General Contractor"
  // Team branch (services/team.js#groupTeamRows) and the per-recipient portal access modal's
  // contact picker. Award already sets this from the winning bid; this lets the team set/fix it
  // for contracts created standalone or missing the link. organizations loads lazily, once, the
  // first time the picker opens.
  const [orgId, setOrgId]                 = useState(null)
  const [organizations, setOrganizations] = useState(null)
  const [orgQuery, setOrgQuery]           = useState('')
  const [pickingOrg, setPickingOrg]       = useState(false)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      // Edit-only: a contract is created by awarding a bidder, never from this modal.
      if (!d.contractId) return
      setEditId(d.contractId)
      const p = d.prefill || {}
      setContractor(p.contractor_name || '')
      setType(p.contract_type || '')
      setValue(p.contract_value != null ? String(p.contract_value) : '')
      setStartDate(p.contract_start_date || '')
      setBuildDate(p.build_start_date || '')
      setSubDate(p.substantial_completion_date || '')
      setStatus(p.status || 'active')
      setNotes(p.notes || '')
      setOrgId(p.contractor_airtable_id || null)
      setOrganizations(null); setOrgQuery(''); setPickingOrg(false)
      setError(null); setSubmitting(false); setOpen(true)
    }
    window.addEventListener('open-add-contract-modal', onOpen)
    return () => window.removeEventListener('open-add-contract-modal', onOpen)
  }, [])

  function openOrgPicker() {
    setPickingOrg(true)
    if (organizations === null) getOrganizations().then(setOrganizations).catch(() => setOrganizations([]))
  }

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  async function handleSubmit() {
    setSubmitting(true); setError(null)
    const fields = {
      contractor_name: contractor || null,
      contractor_airtable_id: orgId || null,
      contract_type: type || null,
      contract_value: value ? Number(value) : null,
      contract_start_date: startDate || null,
      build_start_date: buildDate || null,
      substantial_completion_date: subDate || null,
      status,
      notes: notes || null,
    }
    try {
      if (!editId) { setError('Contracts are created by awarding a bidder.'); setSubmitting(false); return }
      try {
        await updateContract(editId, fields)
      } catch (ex) {
        // A project has at most one active contract (database/209). Rather than a bare error,
        // offer the actual decision — and name what gets closed out, since the active contract is
        // what routes the GC's inbound email and what the Team tab shows as the project's GC.
        if (ex.code !== ACTIVE_CONTRACT_CONFLICT) throw ex
        const other = ex.conflict?.contractorName || 'the existing contract'
        const ok = await confirmDialog({
          title: 'Another contract is already active',
          message: `${other} is the active contract on this project, and a project can only have one at a time. `
            + `Make this the active contract instead? ${other} will be marked Closed Out.`,
          confirmLabel: 'Make this active',
        })
        if (!ok) { setSubmitting(false); return }
        await updateContract(editId, fields, { makeActive: true })
      }
      ctx.contractCache = undefined
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to save contract'); setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddContractModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 440, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>
            Edit Contract
          </span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Contractor (GC)</label>
            <input style={inputStyle} value={contractor} onChange={e => setContractor(e.target.value)} />
          </div>

          <div>
            <label style={labelStyle}>Linked Organization</label>
            {!pickingOrg ? (
              orgId ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)' }}>
                  <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)' }}>
                    {organizations ? ((organizations.find(o => o.id === orgId) || {}).fields || {})['Organization Name'] || orgId : 'Loading…'}
                  </span>
                  <button onClick={openOrgPicker} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 11, fontFamily: '"DM Sans",sans-serif' }}>Change</button>
                  <button onClick={() => setOrgId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', fontSize: 11, fontFamily: '"DM Sans",sans-serif' }}>Unlink</button>
                </div>
              ) : (
                <button onClick={openOrgPicker} style={{ ...inputStyle, textAlign: 'left', cursor: 'pointer', color: 'var(--aj-text-muted)' }}>+ Link to an Organization…</button>
              )
            ) : (
              <div style={{ border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: 8 }}>
                <input style={inputStyle} autoFocus value={orgQuery} placeholder="Search organizations…" onChange={e => setOrgQuery(e.target.value)} />
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 160, overflowY: 'auto' }}>
                  {organizations === null && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', padding: '6px 2px' }}>Loading organizations…</div>}
                  {organizations && organizations
                    .filter(o => !orgQuery.trim() || (o.fields['Organization Name'] || '').toLowerCase().includes(orgQuery.trim().toLowerCase()))
                    .sort((a, b) => (a.fields['Organization Name'] || '').localeCompare(b.fields['Organization Name'] || ''))
                    .slice(0, 30)
                    .map(o => (
                      <button key={o.id} onClick={() => { setOrgId(o.id); setPickingOrg(false); if (!contractor.trim()) setContractor(o.fields['Organization Name'] || '') }}
                        style={{ textAlign: 'left', padding: '6px 8px', borderRadius: 4, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)' }}>
                        {o.fields['Organization Name'] || '(unnamed)'}
                      </button>
                    ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                  <button onClick={() => setPickingOrg(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 11, fontFamily: '"DM Sans",sans-serif' }}>Cancel</button>
                </div>
              </div>
            )}
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', marginTop: 4 }}>
              Drives the derived General Contractor branch in Reference &gt; Team and the recipient picker in Manage portal access.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Contract Type</label>
              <Select value={type} onChange={setType}
                options={[{ value: '', label: '— None —' }].concat(CONTRACT_TYPES.map(t => ({ value: t, label: t })))}
                placeholder="— None —" style={{ width: '100%', fontSize: 12 }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Status</label>
              <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} style={{ width: '100%', fontSize: 12 }} />
            </div>
          </div>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', marginTop: -6 }}>
            Contract Type also sets the boilerplate tone on this contract's issued ASI PDFs — Cost-Plus /
            Time and Materials read collaborative, Lump Sum and other fixed-price types read strict/AIA-style.
          </div>
          <div>
            <label style={labelStyle}>Contract Value ($)</label>
            <input type="number" style={inputStyle} value={value} onChange={e => setValue(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Contract Start</label>
              <input type="date" style={inputStyle} value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Build Start</label>
              <input type="date" style={inputStyle} value={buildDate} onChange={e => setBuildDate(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Substantial Compl.</label>
              <input type="date" style={inputStyle} value={subDate} onChange={e => setSubDate(e.target.value)} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea style={areaStyle} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={submitting} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: submitting ? 'var(--aj-bg-muted)' : '#07272D', color: submitting ? 'var(--aj-text-muted)' : '#fff', border: 'none', borderRadius: 4, cursor: submitting ? 'default' : 'pointer' }}>
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
